import { Box } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import {
  ExpandedMultiSelect,
  FlexCol,
  FlexRow,
  FormSelect,
  MultiSelect,
  NoraButton,
  Textarea,
} from '../../../../common/components/v2';
import { Decision } from '../../../../common/constants/decision';
import { Reason } from '../../../../common/constants/reasons';
import { Resource } from '../../../../common/constants/resource';
import { Metadata } from '../../../../common/dataTypes';
import ElementLocators from '../../../../common/ElementLocators';
import { IDecision } from '../../interfaces/IDecision';
import { ClrCheckbox } from '../components/ClrCheckbox';
import { DecisionUpdateReasons } from '../decisionUpdateReasons';
import { MIBCodebacks } from '../MIBCodebacks';
import styles from '../styles.module.scss';
import usePostponeForm, { PostponeFormData } from './usePostponeForm';

const decisionUpdateOptions = Object.values(DecisionUpdateReasons).map(
  value => ({ value, label: value }),
);

const decisionOptions = [
  { value: Decision.DECLINED, label: Resource.DECLINE_OPTION },
  { value: Decision.APPROVED, label: Resource.APPROVE_OPTION },
  { value: Decision.POSTPONED, label: Resource.POSTPONE_OPTION },
  { value: Decision.INELIGIBLE, label: Resource.INELIGIBLE_OPTION },
  { value: Decision.WAIVED, label: Resource.WAIVED_OPTION },
];

const mapReasonOption = (reason: Reason) => ({
  value: reason.key,
  label: reason.displayName,
});

const mapPostponeConditionOption = (condition: Metadata) => ({
  value: condition.name,
  label: condition.displayText,
});

const PostponeForm = ({
  isUpdate,
  reasons,
  postponeConditions,
  isOpen,
  onSuccess,
  initialFormData,
  onDecisionChanged,
  editedDecision,
}: {
  isUpdate: boolean;
  reasons: Reason[];
  postponeConditions: Metadata[];
  isOpen: boolean;
  onSuccess: (formData: PostponeFormData) => void;
  initialFormData?: PostponeFormData | null;
  onDecisionChanged?: (decision: Decision) => void;
  editedDecision?: IDecision;
}) => {
  const { handleSubmit, control, clear, reset, watch, setValue } =
    usePostponeForm({
      isUpdate,
    });

  const decision = watch('decision');

  const reasonsOptions = useMemo(() => reasons.map(mapReasonOption), [reasons]);
  const postponeConditionsOptions = useMemo(
    () => postponeConditions.map(mapPostponeConditionOption),
    [postponeConditions],
  );

  useEffect(() => {
    if (isOpen) return;
    clear();
  }, [isOpen, clear]);

  useEffect(() => {
    if (!initialFormData) return;
    reset(initialFormData);
  }, [reset, initialFormData]);

  useEffect(() => {
    if (!editedDecision || editedDecision.haltClr === undefined) return;
    setValue('triggerClr', !editedDecision.haltClr);
    setValue('haltClrReasons', editedDecision.haltClrReasons || []);
  }, [editedDecision, setValue]);

  useEffect(() => {
    onDecisionChanged?.(decision);
  }, [onDecisionChanged, decision]);

  return (
    <Box display="flex" flexDirection="column" gap={2} className={styles.DrawerFormContent}>
      <Box flex={1} display="flex" flexDirection="column" gap={3}>
        {isUpdate && (
          <>
            <FlexRow>
              <FlexCol xs={12}>
                <Controller
                  control={control}
                  name="updateReason"
                  render={({ field, fieldState }) => (
                    <FormSelect
                      required
                      label="Reason for update"
                      options={decisionUpdateOptions}
                      error={fieldState.error?.message}
                      {...field}
                    />
                  )}
                />
              </FlexCol>
            </FlexRow>
            <FlexRow>
              <FlexCol xs={12}>
                <Controller
                  control={control}
                  name="decision"
                  render={({ field, fieldState }) => (
                    <FormSelect
                      required
                      label="Decision"
                      options={decisionOptions}
                      error={fieldState.error?.message}
                      {...field}
                    />
                  )}
                />
              </FlexCol>
            </FlexRow>
          </>
        )}
        <ClrCheckbox
          control={control}
          editedDecisionType={editedDecision?.decisionType}
        />
        <FlexRow>
          <FlexCol xs={12}>
            <Controller
              control={control}
              name="mibCodes"
              render={({ field, fieldState }) => (
                <MIBCodebacks
                  label="Mib codes"
                  error={fieldState.error?.message}
                  {...field}
                />
              )}
            />
          </FlexCol>
        </FlexRow>
        <FlexRow>
          <FlexCol xs={12}>
            <Controller
              control={control}
              name="reasons"
              render={({ field, fieldState }) => (
                <ExpandedMultiSelect
                  required
                  options={reasonsOptions}
                  label="Postpone reasons"
                  error={fieldState.error?.message}
                  {...field}
                />
              )}
            />
          </FlexCol>
        </FlexRow>
        <FlexRow>
          <FlexCol xs={12}>
            <Controller
              control={control}
              name="postponeDescription"
              render={({ field, fieldState }) => (
                <Textarea
                  required
                  label="Postpone description"
                  error={fieldState.error?.message}
                  {...field}
                />
              )}
            />
          </FlexCol>
        </FlexRow>
        <FlexRow>
          <FlexCol xs={12}>
            <Controller
              control={control}
              name="postponeConditions"
              render={({ field, fieldState }) => (
                <MultiSelect
                  label="Postpone conditions"
                  options={postponeConditionsOptions}
                  error={fieldState.error?.message}
                  slotProps={{ inputLabel: { shrink: true } }}
                  {...field}
                />
              )}
            />
          </FlexCol>
        </FlexRow>
      </Box>
      <NoraButton
        className={styles.DrawerButton}
        variant="primary"
        name={ElementLocators.LOCATOR_REVIEW_POSTPONE_BUTTON}
        onClick={handleSubmit(onSuccess)}
      >
        {isUpdate ? Resource.REVIEW_DECISION : Resource.REVIEW_APPLICATION}
      </NoraButton>
    </Box>
  );
};

export default PostponeForm;
